import {eventManager} from '@application/Event';
import {UiEvent, UserEvent} from '@domain/model/Event';
import {PageEventView, PageViewEventPayload} from '@domain/model/Event/domain/model/UiEvents';
import {getAuthHeader} from '@infra/api/authManager/authManager';
import {emitUserEvent, initTracking, setTrackingUserId, unsetTrackingUserId} from '@lookiero/user-tracking-front';

import {
  APP_ACTIONS,
  DERISKER_ACTION_TO_SECTION,
  EXPERIMENT_ACTIONS,
  HOME_VIEWS,
  MY_LOOKIERO_EVENTS,
  NAVIGATION_ACTION_TO_NAME,
  NAVIGATION_ACTIONS,
  NAVIGATION_USER_ACTIONS,
  PAY_ACTIONS,
  PAYMENTS_SECTION_TO_TRACKING_SECTION,
  PROMO_CODE_ACTIONS,
  SEGMENT,
  SESSION_ACTION_TO_NAME,
  SESSION_ACTIONS,
  TRANSACTIONAL_ACTION_TO_NAME,
  TRANSACTIONAL_ACTIONS,
  VIEW_TO_SECTION,
} from './tracking.definition';
import {
  cleanUserData,
  getPaymentInstrument,
  getStore,
  getUserData,
  initUserData,
  isLogged,
  UserDataFields,
} from '@tracking/user-data';
import {AuthApplication} from '@application/Auth';
import {Section} from '@lookiero/payments-front';
import {ConfigurationApplication} from '@application/Config';
import {TradeName} from '@domain/model/Config/Configuration';
import {DERISKER_ACTIONS} from '@tracking/tracking.definition';

const getUserParams = async (): Promise<{userId?: string; segment?: string; tradename?: TradeName}> => {
  const {tradename} = ConfigurationApplication.retrieve();

  const logged = await isLogged();

  if (logged) {
    const {id: userId, segment} = await getUserData();
    return {userId, segment, tradename};
  }

  return {segment: SEGMENT.NEUTRAL, tradename};
};

const getEvent = (
  type: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  {promoCode, isFirstOrder, deliveryMethod = '', daysSkipped, ...others}: any,
): Record<string, unknown> => {
  return {
    ecommerce: {
      [type]: {
        actionField: {
          coupon: promoCode,
          ...others,
        },
      },
    },
    isFirstOrder,
    daysSkipped,
    deliveryMethod,
  };
};
type SubscriptionPausedPayload = {daysPaused: number};

const PAYLOAD_TO_EVENT: Record<string, (payload: unknown) => Record<string, unknown>> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [UserEvent.ORDER_CANCELLED]: (payload: any) => getEvent('cancel', payload),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [UserEvent.ORDER_PLACED]: (payload: any) => getEvent('purchase', payload),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [UserEvent.ORDER_SKIPPED]: (payload: any) => getEvent('orderSkipped', payload),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [UserEvent.SUBSCRIPTION_CANCELLED]: (payload: any) => getEvent('cancelSubscription', payload),
  [UserEvent.SUBSCRIPTION_PAUSED]: (payload: unknown) => ({
    daysSkipped: (payload as SubscriptionPausedPayload).daysPaused,
  }),
};

const {userTracker, sendTrackerModel} = initTracking();

eventManager.subscribe(
  UiEvent.PAGE_LOAD,
  async ({view, category, ...payloadExtraInfo}: PageViewEventPayload): Promise<void> => {
    const section = VIEW_TO_SECTION[view];
    if (section) {
      const store = getStore();
      const paymentInstrument = await getPaymentInstrument();
      const userLogged = await isLogged();

      const eventData = {
        paymentMethod: paymentInstrument != null ? paymentInstrument.payment_method : 'none',
        paymentRegistered: paymentInstrument ? 'true' : 'false',
        section: category ? `${category}_${section}` : section,
        store,
        userLogged,
        ...(await getUserParams()),
        ...payloadExtraInfo,
      };

      userTracker(eventData);
    }
  },
);

SESSION_ACTIONS.forEach(action => {
  eventManager.subscribe(action, async payload => {
    const isLogout = action === UserEvent.LOGOUT;
    const isLogin = action === UserEvent.LOGIN;
    const isRegister = action === UserEvent.REGISTER;

    let eventData = {
      event: SESSION_ACTION_TO_NAME[action],
      eventCategory: 'account',
      userLogged: action !== UserEvent.LOGOUT,
      ...payload,
    };

    if (isLogin || isRegister) {
      const {loginMethod} = await AuthApplication.getLoginInfo();
      const {id} = await getUserData();

      setTrackingUserId(id);
      eventData = {
        ...eventData,
        accessVia: loginMethod,
        ...(await getUserParams()),
      };
    } else if (isLogout) {
      unsetTrackingUserId();
    }
    emitUserEvent({...eventData});
  });
});

TRANSACTIONAL_ACTIONS.forEach(action => {
  eventManager.subscribe(action, async payload => {
    const paymentInstrument = await getPaymentInstrument();

    const extra =
      action === UserEvent.ORDER_PLACED
        ? {
            paymentMethod: paymentInstrument != null ? paymentInstrument.payment_method : 'none',
          }
        : {};

    const eventData = {
      event: TRANSACTIONAL_ACTION_TO_NAME[action],
      eventCategory: 'ecommerce',
      ...(await getUserParams()),
      ...PAYLOAD_TO_EVENT[action](payload),
      ...extra,
    };
    emitUserEvent(eventData);
  });
});

interface MyLookieroEventPayload {
  section?: string;
  view: PageEventView;

  [x: string]: unknown;
}

MY_LOOKIERO_EVENTS.forEach(event =>
  eventManager.subscribe(event, async (payload: MyLookieroEventPayload) => {
    const [, eventName] = event.split('.');
    const store = getStore();
    const {view, ...rest} = payload;
    const section = rest.section || VIEW_TO_SECTION[view];

    userTracker({
      event: eventName,
      eventCategory: 'navigation',
      userLogged: true,
      section,
      store,
      ...(await getUserParams()),
      ...rest,
    });
  }),
);

eventManager.subscribe(
  UserEvent.SUBSCRIPTION_UPDATED,
  async ({subscription, newSubscription, previousFrequencyDay, currentFrequencyDay}) => {
    if (subscription !== newSubscription) {
      emitUserEvent({
        event: 'updateSubscription',
        eventCategory: 'ecommerce',
        subscription,
        newSubscription,
        ...(await getUserParams()),
      });
    }
    if ((currentFrequencyDay || previousFrequencyDay) && currentFrequencyDay !== previousFrequencyDay) {
      emitUserEvent({
        event: 'recurrentDeliveryChanged',
        eventCategory: 'ecommerce',
        previousDay: previousFrequencyDay,
        newDay: currentFrequencyDay,
        ...(await getUserParams()),
      });
    }
  },
);

NAVIGATION_ACTIONS.forEach(action => {
  eventManager.subscribe(action, async payload => {
    const eventData = {
      event: NAVIGATION_ACTION_TO_NAME[action],
      eventCategory: 'navigation',
      ...(await getUserParams()),
      ...payload,
    };
    emitUserEvent(eventData);
  });
});

NAVIGATION_USER_ACTIONS.forEach(action => {
  eventManager.subscribe(action, async payload => {
    const eventData = {
      event: NAVIGATION_ACTION_TO_NAME[action],
      eventCategory: 'navigation',
      store: getStore(),
      ...(await getUserParams()),
      ...payload,
    };
    userTracker(eventData);
  });
});

DERISKER_ACTIONS.forEach(action => {
  eventManager.subscribe(action, async payload => {
    const eventData = {
      event: action,
      eventCategory: 'navigation',
      store: getStore(),
      userLogged: await isLogged(),
      section: DERISKER_ACTION_TO_SECTION[action],
      ...(await getUserParams()),
      ...payload,
    };
    userTracker(eventData);
  });
});

eventManager.subscribe(UiEvent.PAGE_LOAD, async ({view}: PageViewEventPayload): Promise<void> => {
  if (HOME_VIEWS.includes(view)) {
    // voyager y pathfinder
    sendTrackerModel({...(await getAuthHeader())});
  }
});

EXPERIMENT_ACTIONS.forEach(action => {
  eventManager.subscribe(action, async payload => {
    const eventData = {
      event: action,
      eventCategory: 'experiment',
      ...(await getUserParams()),
      ...payload,
    };
    emitUserEvent(eventData);
  });
});

APP_ACTIONS.forEach(action => {
  eventManager.subscribe(action, async payload => {
    emitUserEvent({
      event: action,
      eventCategory: 'app',
      ...(await getUserParams()),
      ...payload,
    });
  });
});

PROMO_CODE_ACTIONS.forEach(action => {
  eventManager.subscribe(action, async payload => {
    emitUserEvent({
      event: action,
      eventCategory: 'ecommerce',
      ...(await getUserParams()),
      ...payload,
    });
  });
});

PAY_ACTIONS.forEach(action => {
  eventManager.subscribe(action, async payload => {
    const userData = await getUserData();
    const paymentsSection =
      payload.section === Section.BOX_PLACE_ORDER && !userData.hasAnyOrders
        ? `first-${Section.BOX_PLACE_ORDER}`
        : payload.section;
    const section = PAYMENTS_SECTION_TO_TRACKING_SECTION[paymentsSection];
    userTracker({
      event: action,
      eventCategory: 'ecommerce',
      ...(await getUserParams()),
      store: getStore(),
      userLogged: await isLogged(),
      ...payload,
      section,
    });
  });
});

eventManager.subscribe(UserEvent.USER_LOADED, user => initUserData(user as UserDataFields));
eventManager.subscribe(UserEvent.LOGOUT, () => cleanUserData());
